import axios from 'axios';
import { assign, findIndex, find } from 'lodash-es';
import React, { useState, useCallback, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { mountComponentWithData } from '../../common/mountUtils';
import { clearSessionStorage } from './common/utils';
import { StepsIndicator } from './common/StepsIndicator';
import { Setup, Plans, Details, ReviewAndPayment, Finish, Layout } from './steps';
const stripePromise = loadStripe(process.env.MIX_STRIPE_KEY);
export const updateHubspotContact = async (fields) => {
    var _a, _b, _c, _d;
    try {
        const response = await axios.post('/api/register/update-crm', {
            email: fields.email,
            first_name: fields.firstName,
            last_name: fields.lastName,
            plan: (_a = fields.selectedPlan) === null || _a === void 0 ? void 0 : _a.name,
            phone: fields.phone,
            company: fields.company,
            account_type: fields.accountType,
            country: fields.country,
            city: fields.city,
            state: fields.state,
            address: fields.address,
            zip: fields.zipcode,
            industry: fields.industry,
            promo_code: (_b = fields.promoCode) === null || _b === void 0 ? void 0 : _b.code,
            hs_company_id: fields.hsCompanyId,
            hs_contact_id: fields.hsContactId,
        });
        return {
            hsCompanyId: (_c = response.data) === null || _c === void 0 ? void 0 : _c.hs_company_id,
            hsContactId: (_d = response.data) === null || _d === void 0 ? void 0 : _d.hs_contact_id,
        };
    }
    catch (error) {
        return {
            hsCompanyId: null,
            hsContactId: null,
        };
    }
};
function loadFields() {
    const fieldsString = window.sessionStorage.getItem('fields');
    if (fieldsString) {
        try {
            return JSON.parse(fieldsString);
        }
        catch (error) {
            console.error(error);
            return {};
        }
    }
    return {};
}
export const sections = [
    {
        id: 'plan',
        title: 'Choose Plan',
        component: Plans,
    },
    {
        id: 'setup',
        title: 'Account set-up',
        component: Setup,
    },
    {
        id: 'details',
        title: 'Company/Personal Details',
        component: Details,
    },
    {
        id: 'review',
        title: 'Review/Payment',
        component: ReviewAndPayment,
    },
    {
        id: 'finish',
        title: 'Start Tracking',
        component: Finish,
    },
];
const initialFieldValues = {
    accountType: null,
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    phone: '',
    address: '',
    country: 'US',
    city: '',
    state: 'AL',
    zipcode: '',
    password: '',
    password_confirmation: '',
    plans: [],
    selectedPlan: null,
    cardName: '',
    cardCountry: 'US',
    cardAddress: '',
    cardCity: '',
    cardState: '',
    cardZipcode: '',
    promoCode: {
        code: '',
        discountPercentage: 0,
    },
    paymentMethodId: null,
    taxes: null,
    privacyAccepted: false,
    emailVerificationCode: '',
    industry: '',
    emailVerified: '',
    otpFirstGenerated: false,
    hsCompanyId: null,
    hsContactId: null,
};
const debounceSaveFields = AwesomeDebouncePromise((fields) => {
    window.sessionStorage.setItem('fields', JSON.stringify(fields));
}, 500);
function RegisterPage({ plans }) {
    /* set up pre-selected plan */
    const initialFields = assign({}, initialFieldValues, loadFields());
    initialFields.plans = plans;
    if (!initialFields.selectedPlan) {
        const selectedPlan = (new URLSearchParams(window.location.search)).get('plan') || '';
        const preselectedSelectedPlan = find(plans, { name: selectedPlan }) || null;
        initialFields.selectedPlan = preselectedSelectedPlan;
    }
    /* fields */
    const [fields, setFields] = useState(initialFields);
    const [isInterfaceLoading, setIsInterfaceLoading] = useState(false);
    const [hasFinished, setHasFinished] = useState(false);
    const updateFields = useCallback((newFields) => {
        setFields(oldFields => assign({}, oldFields, newFields));
    }, [setFields]);
    useEffect(() => {
        debounceSaveFields(fields);
    }, [fields]);
    /* UI config */
    const [currentSectionIndex, setCurrentSectionIndex] = useState(() => {
        const sectionIndex = Number(window.sessionStorage.getItem('currentSectionIndex'));
        // skip step 0 if the plan is preselected
        if (sectionIndex === 0 && fields.selectedPlan) {
            return 1;
        }
        return sectionIndex;
    });
    const previousSection = useCallback(() => {
        const index = Math.max(currentSectionIndex - 1, 0);
        setCurrentSectionIndex(index);
        window.sessionStorage.setItem('currentSectionIndex', String(index));
        window.scrollTo(0, 0);
        setIsInterfaceLoading(false);
    }, [currentSectionIndex, setCurrentSectionIndex, setIsInterfaceLoading]);
    const nextSection = useCallback(async () => {
        const index = Math.min(currentSectionIndex + 1, sections.length - 1);
        setCurrentSectionIndex(index);
        window.sessionStorage.setItem('currentSectionIndex', String(index));
        window.scrollTo(0, 0);
        setIsInterfaceLoading(false);
    }, [currentSectionIndex, setCurrentSectionIndex, setIsInterfaceLoading]);
    const navigateToSection = useCallback((sectionName) => {
        if (hasFinished) {
            return;
        }
        const index = findIndex(sections, { id: sectionName });
        setCurrentSectionIndex(index);
        window.sessionStorage.setItem('currentSectionIndex', String(index));
        window.scrollTo(0, 0);
        setIsInterfaceLoading(false);
    }, [hasFinished, currentSectionIndex, setCurrentSectionIndex, setIsInterfaceLoading]);
    /* submit */
    const finishCalback = useCallback(() => {
        nextSection();
        clearSessionStorage();
        setFields(initialFieldValues);
        setIsInterfaceLoading(false);
        setHasFinished(true);
    }, [fields, nextSection, setIsInterfaceLoading, setHasFinished]);
    const Step = sections[currentSectionIndex].component;
    return (React.createElement(Elements, { stripe: stripePromise },
        React.createElement(React.Fragment, null,
            (currentSectionIndex + 1) <= sections.length && (React.createElement(StepsIndicator, { steps: sections, currentStepIndex: currentSectionIndex, clickStepHandler: navigateToSection })),
            currentSectionIndex >= 1 && fields.selectedPlan && (React.createElement("div", { className: 'register-step-layout__confirmation' },
                React.createElement("strong", null,
                    "Chosen Plan: ",
                    React.createElement("span", null, fields.selectedPlan.name),
                    fields.accountType && React.createElement("span", null, fields.accountType)),
                React.createElement("p", null, "You can change your plan again in the future"))),
            React.createElement(Layout, { isLoading: isInterfaceLoading },
                React.createElement(Step, { fields: fields, updateFields: updateFields, previousSection: previousSection, nextSection: nextSection, navigateToSection: navigateToSection, finishCallback: finishCalback, onLoading: (value) => setIsInterfaceLoading(value), isLoading: isInterfaceLoading })))));
}
window.addEventListener('load', () => {
    mountComponentWithData('register-root', props => React.createElement(RegisterPage, Object.assign({}, props)));
});
